import { siteDay, siteInstant, shiftDay } from "@/lib/site-time";
/**
 * Analytics cron endpoint, protected with the managed cron secret (Bearer).
 * body {task:"archive"}  -> monthly totals + delete details older than 36 months
 * body {task:"weekly"}   -> weekly summary mail to info@goproprojects.be
 */
import { createFileRoute } from "@tanstack/react-router";
import { authenticateCronRequest } from "@/integrations/supabase/cron-auth";

const RESEND_GATEWAY_URL = "https://connector-gateway.lovable.dev/resend";

function pct(now: number, before: number) {
  if (!before) return now ? "nieuw" : "gelijk";
  const d = Math.round(((now - before) / before) * 100);
  return `${d >= 0 ? "+" : ""}${d}%`;
}

async function weekly() {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { fetchEvents, filterRows } = await import("@/lib/analytics-admin.functions");
  const { buildReport } = await import("@/lib/analytics-report");
  const today = siteDay();
  const to = siteInstant(today);
  const from = siteInstant(shiftDay(today, -7));
  const prev = siteInstant(shiftDay(today, -14));
  const [cur, before] = await Promise.all([
    fetchEvents(supabaseAdmin, from.toISOString(), to.toISOString()),
    fetchEvents(supabaseAdmin, prev.toISOString(), from.toISOString()),
  ]);
  const r = buildReport(filterRows(cur, { showBots: false }), filterRows(before, { showBots: false }), cur.length);
  const lovableKey = process.env["LOVABLE_API_KEY"];
  const resendKey = process.env["RESEND_API_KEY"];
  if (!lovableKey || !resendKey) return { ok: false, reason: "e-mailverzending niet ingesteld" };
  const list = (items: { key: string; count: number }[]) =>
    items.slice(0, 5).map((i) => `<li>${i.key.replace(/[<>&]/g, "")}: ${i.count}</li>`).join("") || "<li>geen</li>";
  const html = [
    `<p>Uw weekoverzicht van goproprojects.be (afgelopen 7 dagen).</p>`,
    `<p><strong>Bezoeken:</strong> ${r.sessions} (${pct(r.sessions, r.previous.sessions)})<br>`,
    `<strong>Bekeken pagina's:</strong> ${r.pageViews} (${pct(r.pageViews, r.previous.pageViews)})<br>`,
    `<strong>Offerte-aanvragen:</strong> ${r.leads} (${pct(r.leads, r.previous.leads)})</p>`,
    `<p><strong>Herkomst</strong></p><ul>${list(r.sources)}</ul>`,
    `<p><strong>Meest bekeken</strong></p><ul>${list(r.topPages)}</ul>`,
    `<p><strong>Klikken op telefoon, e-mail en WhatsApp</strong></p><ul>${list(r.contactClicks)}</ul>`,
    `<p>Alle details vindt u in het beheer onder Statistieken.</p>`,
  ].join("");
  const res = await fetch(`${RESEND_GATEWAY_URL}/emails`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${lovableKey}`, "X-Connection-Api-Key": resendKey },
    body: JSON.stringify({
      from: "GoPro Projects <noreply@info.goproprojects.be>",
      to: ["info@goproprojects.be"],
      subject: `Weekoverzicht website: ${r.sessions} bezoeken, ${r.leads} aanvragen`,
      html,
    }),
  });
  if (!res.ok) {
    const body = await res.text();
    console.error(`[analytics-weekly] mail failed [${res.status}]: ${body}`);
    return { ok: false, status: res.status };
  }
  return { ok: true };
}

async function run(request: Request) {
  const denied = await authenticateCronRequest(request);
  if (denied) return denied;
  let task: unknown = null;
  try {
    task = ((await request.json()) as { task?: unknown }).task;
  } catch {
    task = null;
  }
  try {
    if (task === "archive") {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const { data, error } = await supabaseAdmin.rpc("analytics_archive");
      if (error) throw new Error(error.message);
      return Response.json({ ok: true, deleted: data });
    }
    if (task === "weekly") return Response.json(await weekly());
    return Response.json({ ok: false, error: "onbekende taak" }, { status: 400 });
  } catch (error) {
    console.error("[analytics-cron]", (error as Error).message);
    return Response.json({ ok: false }, { status: 500 });
  }
}

export const Route = createFileRoute("/api/public/analytics-cron")({
  staticData: { sitemap: false },
  server: { handlers: { POST: async ({ request }) => run(request) } },
});
